/**
 * Saving an authenticated response as a file.
 *
 * Every download in this app goes through the API client (JWT + tenant headers),
 * so none of them can be a plain `<a href>` — the bytes arrive as a Blob and the
 * browser has to be talked into saving them. These two helpers are that talking.
 */

/** The filename the SERVER chose, read off Content-Disposition.
 *
 *  Preferring the server's name means the side that produced the bytes is the
 *  side that names them, so a name can never disagree with what is actually
 *  inside the file. (The bug this replaced: an `xlsx` artifact fell through a
 *  client-side pptx/docx/html chain to `.md` and downloaded as markdown.)
 *
 *  Returns null when the header is absent or unparseable, leaving the caller's
 *  own name as the fallback.
 */
export function serverFileName(disposition?: string): string | null {
  if (!disposition) return null;
  // RFC 5987 `filename*=UTF-8''...` first — it carries non-ASCII names
  // (Hebrew titles) that the plain `filename=` fallback mangles.
  const star = /filename\*=(?:UTF-8|utf-8)''([^;]+)/i.exec(disposition);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      /* fall through to the plain form */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain?.[1]?.trim() || null;
}

/** Read Content-Disposition off an axios response, falling back to `fallback`. */
export function filenameFrom(headers: unknown, fallback: string): string {
  const disposition = (headers as Record<string, string> | undefined)?.['content-disposition'];
  return serverFileName(disposition) || fallback;
}

/** The server's error message from a request made with `responseType: 'blob'`.
 *
 *  Axios honours `responseType` for failures too, so `error.response.data` is a
 *  Blob of JSON rather than the parsed object — reading `.error` off it silently
 *  yields undefined and every failure collapses into the caller's generic
 *  fallback. Read the blob back to get the real reason.
 */
export async function blobErrorMessage(err: any): Promise<string | null> {
  const data = err?.response?.data;
  if (!(data instanceof Blob)) return data?.error ?? null;
  try {
    return JSON.parse(await data.text())?.error ?? null;
  } catch {
    return null;
  }
}

/** The server's machine-readable `error_code` from a blob request (the
 *  coded failures a caller translates), or null. */
export async function blobErrorCode(err: any): Promise<string | null> {
  const data = err?.response?.data;
  if (!(data instanceof Blob)) return data?.error_code ?? null;
  try {
    return JSON.parse(await data.text())?.error_code ?? null;
  } catch {
    return null;
  }
}

/** The whole JSON error body of a blob request (error_code and its numbers),
 *  or null. */
export async function blobErrorBody(err: any): Promise<Record<string, any> | null> {
  const data = err?.response?.data;
  if (!(data instanceof Blob)) return data && typeof data === 'object' ? data : null;
  try {
    const body = JSON.parse(await data.text());
    return body && typeof body === 'object' ? body : null;
  } catch {
    return null;
  }
}

export function triggerBlobDownload(data: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
